import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertTriangle, XCircle, Percent, ShieldAlert, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import DashboardKpiCard from '@/components/ux/DashboardKpiCard';
import { getOverallStats, updateSubscription, getActivePlans } from '@/services/apiClient';
import { toast } from 'sonner';

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function ActivateModal({ open, onClose, company, onSuccess }) {
    const [plans, setPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            getActivePlans().then(r => setPlans(r.data || [])).catch(() => {});
        }
    }, [open]);

    const handleActivate = async () => {
        if (!selectedPlanId) { toast.error('Select a plan.'); return; }
        setLoading(true);
        try {
            await updateSubscription(company.companyId, { action: 'activate', planId: selectedPlanId });
            toast.success(`${company.companyName} activated.`);
            onSuccess();
        } catch (err) {
            toast.error(err.message || 'Failed to activate.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle>Activate — {company?.companyName}</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 py-2">
                    {plans.map(p => (
                        <button
                            key={p._id}
                            type="button"
                            onClick={() => setSelectedPlanId(p._id)}
                            className={`w-full rounded-lg border px-4 py-2.5 text-left transition-colors ${
                                selectedPlanId === p._id
                                    ? 'border-primary bg-primary/5'
                                    : 'border-border hover:border-muted-foreground/50'
                            }`}
                        >
                            <div className="flex justify-between text-sm">
                                <span className="font-medium">{p.name}</span>
                                <span className="text-muted-foreground">₹{p.price.toLocaleString('en-IN')}/mo</span>
                            </div>
                        </button>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
                    <Button onClick={handleActivate} disabled={loading || !selectedPlanId}>
                        {loading ? 'Activating...' : 'Activate'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Companies the lifecycle sweep refused to act on.
 *
 * Their trial or grace period lapsed so long ago that nothing was watching, so
 * expiring them now would penalise the customer for our gap in coverage. They
 * keep full access until someone decides. Separated from the routine tables
 * because the required action is a judgement call, not maintenance.
 */
function NeedsReviewTable({ rows, onActivate, onExtend, onExpire, loading }) {
    const navigate = useNavigate();

    if (rows.length === 0) {
        return (
            <p className="py-6 text-center text-sm text-muted-foreground">
                Nothing waiting on a decision.
            </p>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-border">
                        <th className="pb-3 text-left font-medium text-muted-foreground">Company</th>
                        <th className="pb-3 text-left font-medium text-muted-foreground">Plan</th>
                        <th className="pb-3 text-left font-medium text-muted-foreground">Held at</th>
                        <th className="pb-3 text-left font-medium text-muted-foreground">Lapsed</th>
                        <th className="pb-3 text-right font-medium text-muted-foreground">Decision</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map(c => (
                        <tr key={c.companyId}>
                            <td className="py-3">
                                <button
                                    type="button"
                                    className="font-medium text-primary hover:underline"
                                    onClick={() => navigate(`/super-admin/companies/${c.companyId}`)}
                                >
                                    {c.companyName}
                                </button>
                                {c.contactEmail && (
                                    <p className="text-xs text-muted-foreground">{c.contactEmail}</p>
                                )}
                            </td>
                            <td className="py-3 text-muted-foreground">{c.planName || '—'}</td>
                            <td className="py-3">
                                <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                                    {c.heldAtStatus === 'grace_period' ? 'Grace period' : 'Trial'}
                                </span>
                            </td>
                            <td className="py-3">
                                <span className="font-semibold text-destructive">
                                    {c.daysLapsed !== null ? `${c.daysLapsed}d ago` : '—'}
                                </span>
                                <p className="text-xs text-muted-foreground">{formatDate(c.lapsedAt)}</p>
                            </td>
                            <td className="py-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                    {c.heldAtStatus === 'trial' && (
                                        <Button
                                            size="sm" variant="outline" className="h-7 text-xs"
                                            disabled={loading}
                                            onClick={() => onExtend(c)}
                                        >
                                            Extend
                                        </Button>
                                    )}
                                    <Button
                                        size="sm" variant="outline"
                                        className="h-7 text-xs text-destructive hover:text-destructive"
                                        disabled={loading}
                                        onClick={() => onExpire(c)}
                                    >
                                        Expire
                                    </Button>
                                    <Button size="sm" className="h-7 text-xs" onClick={() => onActivate(c)}>
                                        Activate
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CompanyTable({ rows, type, onExtend, onActivate, loading }) {
    const navigate = useNavigate();

    if (rows.length === 0) {
        return (
            <p className="py-6 text-center text-sm text-muted-foreground">
                No companies in this category.
            </p>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-border">
                        <th className="pb-3 text-left font-medium text-muted-foreground">Company</th>
                        <th className="pb-3 text-left font-medium text-muted-foreground">Plan</th>
                        {type === 'trial' && (
                            <>
                                <th className="pb-3 text-left font-medium text-muted-foreground">Trial Start</th>
                                <th className="pb-3 text-left font-medium text-muted-foreground">Trial End</th>
                            </>
                        )}
                        {type === 'grace' && (
                            <th className="pb-3 text-left font-medium text-muted-foreground">Grace Ends</th>
                        )}
                        <th className="pb-3 text-center font-medium text-muted-foreground">Days Left</th>
                        <th className="pb-3 text-right font-medium text-muted-foreground">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map(c => {
                        const urgent = c.daysLeft !== null && c.daysLeft <= 3;
                        return (
                            <tr key={c.companyId}>
                                <td className="py-3">
                                    <button
                                        type="button"
                                        className="font-medium text-primary hover:underline"
                                        onClick={() => navigate(`/super-admin/companies/${c.companyId}`)}
                                    >
                                        {c.companyName}
                                    </button>
                                </td>
                                <td className="py-3 text-muted-foreground">{c.planName || '—'}</td>
                                {type === 'trial' && (
                                    <>
                                        <td className="py-3 text-muted-foreground">{formatDate(c.trialStartDate)}</td>
                                        <td className="py-3 text-muted-foreground">{formatDate(c.trialEndDate)}</td>
                                    </>
                                )}
                                {type === 'grace' && (
                                    <td className="py-3 text-muted-foreground">{formatDate(c.gracePeriodEndDate)}</td>
                                )}
                                <td className="py-3 text-center">
                                    {c.daysLeft !== null ? (
                                        <span className={`font-semibold ${urgent ? 'text-destructive' : 'text-foreground'}`}>
                                            {c.daysLeft}d
                                        </span>
                                    ) : '—'}
                                </td>
                                <td className="py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        {type === 'trial' && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-xs"
                                                disabled={loading}
                                                onClick={() => onExtend(c)}
                                            >
                                                +14 days
                                            </Button>
                                        )}
                                        <Button
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => onActivate(c)}
                                        >
                                            Activate
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Companies that have already lost write access.
 *
 * Previously counted in a KPI but listed nowhere, so reactivating one meant
 * knowing to search Companies by hand. These are the accounts most likely to be
 * waiting on a reply, so they belong on the same page as the trials.
 */
function ExpiredTable({ rows, onActivate }) {
    const navigate = useNavigate();

    if (rows.length === 0) {
        return (
            <p className="py-6 text-center text-sm text-muted-foreground">
                No expired accounts.
            </p>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-border">
                        <th className="pb-3 text-left font-medium text-muted-foreground">Company</th>
                        <th className="pb-3 text-left font-medium text-muted-foreground">Plan</th>
                        <th className="pb-3 text-left font-medium text-muted-foreground">State</th>
                        <th className="pb-3 text-left font-medium text-muted-foreground">Blocked since</th>
                        <th className="pb-3 text-right font-medium text-muted-foreground">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map(c => (
                        <tr key={c.companyId}>
                            <td className="py-3">
                                <button
                                    type="button"
                                    className="font-medium text-primary hover:underline"
                                    onClick={() => navigate(`/super-admin/companies/${c.companyId}`)}
                                >
                                    {c.companyName}
                                </button>
                                {c.contactEmail && (
                                    <p className="text-xs text-muted-foreground">{c.contactEmail}</p>
                                )}
                            </td>
                            <td className="py-3 text-muted-foreground">{c.planName || '—'}</td>
                            <td className="py-3">
                                <span className="rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                                    {c.status}
                                </span>
                            </td>
                            <td className="py-3 text-muted-foreground">
                                {c.daysSince !== null ? `${c.daysSince}d` : '—'}
                                <span className="ml-1 text-xs">({formatDate(c.endedAt)})</span>
                            </td>
                            <td className="py-3 text-right">
                                <Button size="sm" className="h-7 text-xs" onClick={() => onActivate(c)}>
                                    Reactivate
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function SuperAdminTrialsPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [activateTarget, setActivateTarget] = useState(null);

    const fetchStats = () => {
        setLoading(true);
        getOverallStats()
            .then(r => setStats(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchStats(); }, []);

    const handleExtend = async (company) => {
        setActionLoading(true);
        try {
            await updateSubscription(company.companyId, { action: 'extend_trial' });
            toast.success(`Trial extended for ${company.companyName}.`);
            fetchStats();
        } catch (err) {
            toast.error(err.message || 'Failed to extend trial.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleExpire = async (company) => {
        setActionLoading(true);
        try {
            await updateSubscription(company.companyId, { action: 'expire' });
            toast.success(`${company.companyName} marked expired.`);
            fetchStats();
        } catch (err) {
            toast.error(err.message || 'Failed to expire.');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
        );
    }

    const trialDetails = stats?.trialCompanyDetails || [];
    const graceDetails = stats?.graceCompanyDetails || [];
    const reviewDetails = stats?.needsReviewDetails || [];
    const expiredDetails = stats?.expiredCompanyDetails || [];
    const expiredCount = stats?.expiredCompanies || 0;
    const totalCompanies = stats?.totalCompanies || 1;
    const expiringUrgent = trialDetails.filter(c => c.daysLeft !== null && c.daysLeft <= 3).length;

    const conversionRate = totalCompanies > 0
        ? Math.round(((totalCompanies - (stats?.trialCompanies || 0) - (stats?.gracePeriodCompanies || 0) - expiredCount) / totalCompanies) * 100)
        : 0;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-foreground">Trials & Subscriptions</h1>
                <p className="text-sm text-muted-foreground">Manage trial companies and subscription status</p>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                <DashboardKpiCard
                    title="Active Trials"
                    value={trialDetails.length}
                    subtitle="Companies on trial"
                    icon={Clock}
                    tone="primary"
                />
                <DashboardKpiCard
                    title="Expiring Soon"
                    value={expiringUrgent}
                    subtitle="Trial ends ≤ 3 days"
                    icon={AlertTriangle}
                    tone={expiringUrgent > 0 ? 'warning' : 'default'}
                />
                <DashboardKpiCard
                    title="Grace Period"
                    value={graceDetails.length}
                    subtitle="At risk of expiry"
                    icon={XCircle}
                    tone={graceDetails.length > 0 ? 'destructive' : 'default'}
                />
                <DashboardKpiCard
                    title="Needs Review"
                    value={reviewDetails.length}
                    subtitle="Awaiting your decision"
                    icon={ShieldAlert}
                    tone={reviewDetails.length > 0 ? 'warning' : 'default'}
                />
                <DashboardKpiCard
                    title="Conversion Rate"
                    value={`${conversionRate}%`}
                    subtitle="Trial → Active"
                    icon={Percent}
                    tone="success"
                />
            </div>

            {/* Needs Review — first, because these are the only rows that are stuck */}
            {reviewDetails.length > 0 && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-6 space-y-4">
                    <div className="space-y-2">
                        <h2 className="flex items-center gap-2 font-semibold text-foreground">
                            <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                            Needs Review ({reviewDetails.length})
                        </h2>
                        <p className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                                These deadlines passed while nothing was checking them. Rather than
                                cut off customers for a gap on our side, the system froze each one
                                with full access intact. They stay exactly as they are until you
                                choose — nothing expires on its own.
                            </span>
                        </p>
                    </div>
                    <NeedsReviewTable
                        rows={reviewDetails}
                        onExtend={handleExtend}
                        onExpire={handleExpire}
                        onActivate={c => setActivateTarget(c)}
                        loading={actionLoading}
                    />
                </div>
            )}

            {/* Active Trials */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
                <h2 className="font-semibold text-foreground">Active Trials ({trialDetails.length})</h2>
                <CompanyTable
                    rows={trialDetails}
                    type="trial"
                    onExtend={handleExtend}
                    onActivate={c => setActivateTarget(c)}
                    loading={actionLoading}
                />
            </div>

            {/* Expired — already blocked, so the shortest path back matters */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
                <div>
                    <h2 className="font-semibold text-foreground">Expired ({expiredDetails.length})</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        These companies can still sign in and read their data, but every change is
                        blocked until a plan is active. Interviews already scheduled still run for
                        their candidates.
                    </p>
                </div>
                <ExpiredTable rows={expiredDetails} onActivate={c => setActivateTarget(c)} />
            </div>

            {/* Grace Period */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
                <h2 className="font-semibold text-foreground">Grace Period ({graceDetails.length})</h2>
                <CompanyTable
                    rows={graceDetails}
                    type="grace"
                    onExtend={handleExtend}
                    onActivate={c => setActivateTarget(c)}
                    loading={actionLoading}
                />
            </div>

            <ActivateModal
                open={!!activateTarget}
                onClose={() => setActivateTarget(null)}
                company={activateTarget}
                onSuccess={() => { setActivateTarget(null); fetchStats(); }}
            />
        </div>
    );
}
